import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import Card from 'antd/lib/card';
import Rate from 'antd/lib/rate';
import Badge from 'antd/lib/badge';
import Carousel from 'antd/lib/carousel';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Skeleton from 'antd/lib/skeleton';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import theme from 'antd/lib/theme';
import classNames from 'classnames';
import Image from 'antd/lib/image';
import { MarketplaceRecordWithRatings } from '@kemu-io/kemu-types';
import { ValidPlatformArch } from '@kemu-io/hs-types';
import { Result, Button, Alert } from 'antd';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import { useDispatch } from 'react-redux';
import HubServiceInstallButton, {
  InstallInfo,
} from '../HubServiceInstallButton/HubServiceInstallButton';
import styles from './HubServiceDetailsPage.module.css';
import SvgFromContent from '@components/SvgFromContent/SvgFromContent';
import useTranslation from '@hooks/useTranslation';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer';
import marketplaceApi from '@src/api/marketplace/marketplaceApi';
import useInstallMarketplaceItem from '@hooks/useInstallMarketplaceItem';
import useIsServiceInstalled from '@hooks/useIsServiceInstalled';
import globals from '@common/globals';
import useMarketplaceFetch from '@hooks/useMarketplaceFetch';
import { isDevEnvironment } from '@common/utils';
import { MARKETPLACE_SUBCATEGORY_BAR_ID } from '@common/constants';
import useSelectMdFile from '@hooks/useSelectMdFile';
import { BasicServiceInfo } from '@hooks/useMarketplaceNavigator';
import useGetPendingServiceInstallation from '@hooks/useGetPendingServiceInstallation';
import { openCheckoutModalAction } from '@src/app/reducers/checkout/checkoutSlice';

type Props = {
  info: {
    name: string;
    version: string;
    platform: string;
  }
  onGoBack: () => void;
  onNavigateToVersion: (serviceInfo: BasicServiceInfo) => void;
  /** Whether user can install more official services */
  canInstallMoreServices?: boolean;
  /** Ask parent to show upgrade CTA */
  onRequestUpgrade?: () => void;
}

const marketplacePublicationUrl = globals.MARKETPLACE_PUBLIC_URL;
const getFullAssetPath = (assetPath: string) => {
  return `${marketplacePublicationUrl}/${assetPath}`;
};

const Container = forwardRef<HTMLDivElement, { children: React.ReactNode }>((props, ref) => {
  return (
    <div className={styles.DetailsPageContainer} ref={ref}>
      <div className={styles.PageWrapper}>
        <div className={styles.PageBody}>
          {props.children}
        </div>
      </div>
    </div>
  );
});

const HubServiceDetailsPage = (props: Props) => {
  const { name, version, platform } = props.info;
  const { pendingServices, isLoading: isPendingServicesLoading } = useGetPendingServiceInstallation();
  const { onNavigateToVersion } = props;
  const { token: { colorPrimary } } = theme.useToken();
  // const { online } = useKemuHubLink();
  const fetchingDataRef = useRef<BasicServiceInfo | null>(null);
  const [serviceDetails, setServiceDetails] = useState<MarketplaceRecordWithRatings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslation('Marketplace.ServiceDetailPage');
  const fetchMarketplace = useMarketplaceFetch();
  const [submitRating, setSubmitRating] = useState(false);
  const loadMdFile = useSelectMdFile();
  const isServiceInstalled = useIsServiceInstalled();
  const [notFound, setNotFound] = useState(false);
  const {
    installedWithSameVersion,
    installedWithLowerVersion,
    installedWithMissingSecrets,
  } = isServiceInstalled(name, version);

  const inDevMode = isDevEnvironment();
  const alreadyInstalled = installedWithSameVersion || installedWithMissingSecrets || installedWithLowerVersion;

  const isInstalling = (serviceInfo?: MarketplaceRecordWithRatings | null) => {
    if (!serviceInfo) {
      return false;
    }

    const status = Object.values(pendingServices).find(service => service.name === serviceInfo.name && service.version === serviceInfo.version)?.status;
    return status === 'installing' || status === 'launching';
  };

  const { installHubService, installingService } = useInstallMarketplaceItem();
  const installing = installingService || isInstalling(serviceDetails);
  const cw = t.withBaseKey('CommonWords');
  const dispatch = useDispatch();
  // const { services } = useActiveHubServices();
  // const officialServices = services.available.filter((service) => !service.internal && !service.devMode && service.name.startsWith('kemu.io')) || [];
  // const subscriptionInfo = useSubscriptionInfo();
  // const remainingOfficialServices = officialServices.length;
  // const canInstallMoreServices = props.canInstallMoreServices ?? ((subscriptionInfo?.features?.['hub_services'] || 0) > remainingOfficialServices);

  const containerRef = useRef<HTMLDivElement>(null);

  const fetchServiceInfo = useCallback(async (config: { name: string, version: string, platform: ValidPlatformArch }) => {
    const { name, version, platform } = config;
    fetchingDataRef.current = config;

    setIsLoading(true);
    const results = await fetchMarketplace({
      names: [name],
			version,
			platform: platform as ValidPlatformArch,
			from: 0,
			total: 1,
			markdown: true,
		}).finally(() => {
      setIsLoading(false);
		});

    if (results.length > 0) {

      setServiceDetails(results[0]);
    } else {
      setNotFound(true);
    }
  }, [fetchMarketplace]);

  const handleRatingChange = async (value: number) => {
    setSubmitRating(true);
    const response = await marketplaceApi.ratePublication({
      // publicationId: (serviceInfo as any).objectId || serviceInfo.id,
      publicationId: (serviceDetails as any).objectId || serviceDetails?.id,
      rating: value,
    });

    if (serviceDetails) {
      setServiceDetails({
        ...serviceDetails,
        rating: {
          average: response.stats.average,
          total: response.stats.total,
        },
      });
    }

    setSubmitRating(false);
  };


  const handleInstallService = (info: InstallInfo) => {
    if (info.installedWithSameVersion || !serviceDetails?.id) {
      return;
    }

    /* if (!canInstallMoreServices) {
      props.onRequestUpgrade?.();
      return;
    } */

    installHubService({
      marketplaceItemId: serviceDetails.id,
      title: serviceDetails?.title,
      name: serviceDetails.name,
      version: serviceDetails.version,
    });
  };

  const handleUpgradeNow = () => {
    dispatch(openCheckoutModalAction());
  };

  const handleFileSelection = async () => {
    const { file } = await loadMdFile();
    if (file && serviceDetails) {
      setServiceDetails({
        ...serviceDetails,
        docs: {
          ...serviceDetails.docs,
          contentMd: file.content
        }
      });
    }
  };

  const handleExploreLatestVersion = () => {
    if (serviceDetails?.latest) {
      onNavigateToVersion({
        name: serviceDetails.name,
        version: serviceDetails.latest.version,
        platform: platform as ValidPlatformArch
      });
    }
  };

  /**
   * Intercept link clicks and handle anchor links within the description
   */
  const handleLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, rawUrl: string | undefined) => {
    // Handle anchor links (internal navigation within the same document)
    if (rawUrl?.startsWith('#')) {
      event.preventDefault();
      const targetId = rawUrl.substring(1);
      const element = document.getElementById(targetId);

      if (element && containerRef.current?.parentElement) {
        // Get the fixed header height (SubCategoryBar)
        const subCategoryBar = document.getElementById(MARKETPLACE_SUBCATEGORY_BAR_ID);
        const headerHeight = subCategoryBar ? subCategoryBar.offsetHeight : 56; // fallback to 56px

        // Scroll to the element, accounting for the fixed header
        containerRef.current.parentElement.scrollTo({
          top: element.offsetTop - headerHeight,
          behavior: 'smooth'
        });
      }
    }
  }, []);

  useEffect(() => {
    const isSameService = fetchingDataRef.current?.name === name
      && fetchingDataRef.current?.version === version
      && fetchingDataRef.current?.platform === platform;

    if (!isLoading && !isPendingServicesLoading && (!fetchingDataRef.current || !isSameService)) {
      fetchServiceInfo({
        name,
        version,
        platform: platform as ValidPlatformArch
      });
    }
  }, [fetchServiceInfo, isLoading, name, version, platform, isPendingServicesLoading]);

  const currentHash = window.location.hash;

  useEffect(() => {
      if (currentHash && !notFound && !isLoading) {
        const element = document.getElementById(currentHash.replace('#', ''));
        if (element && containerRef.current?.parentElement) {
          containerRef.current.parentElement.scrollTo({
            top: element.offsetTop,
            behavior: 'smooth'
          });
        }
      }
    }, [currentHash, notFound, isLoading]);

  if (notFound) {
    return (
      <Container>
        <Result
          status="404"
          title={t('NotFound.Title')}
          subTitle={t('NotFound.Description')}
          extra={<Button type="primary" onClick={props.onGoBack}>{t('NotFound.BtnBack')}</Button>}
        />
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      {isLoading ? (
            <LoadingState />
      ) : serviceDetails && (
        <Card>
          {/* Header */}
          <div className={styles.Header}>
            <div className={styles.IconWrapper}>
              <SvgFromContent content={serviceDetails.iconSvg.trim()} height={95} width={95} />
            </div>

            <div className={styles.TitleSection}>
              <div className={styles.Title}>{serviceDetails.title}</div>
              <div className={styles.Version}>
                Version {serviceDetails.version}
              </div>
              <div className={styles.Author}>
                {serviceDetails.isKemuOfficial && (
                  <Tooltip title={`${cw('KemuOfficial')} ${cw('Account')}`}>
                    <Badge
                      count={<SafetyCertificateOutlined style={{ color: colorPrimary }} />}
                    />
                  </Tooltip>
                )}
                {serviceDetails.author.name}
              </div>
              <div className={styles.Rating}>
                <Rate
                  value={serviceDetails.rating.average}
                  onChange={handleRatingChange}
                  allowHalf
                  allowClear={false}
                  disabled={submitRating}
                />
                <span className={styles.ReviewCount}>({serviceDetails.rating.total})</span>
              </div>
            </div>

            <div className={styles.InstallBtnContainer}>
              <HubServiceInstallButton
                serviceName={serviceDetails.name}
                serviceVersion={serviceDetails.version}
                installingService={!!installing}
                handleInstallService={handleInstallService}
                notLatestVersion={!!serviceDetails.latest}
              />
            </div>

          </div>

          {/* CTA modal moved to parent (HubServicesScreen) */}

          {serviceDetails.latest && (
            <Alert
              message={t('NewerVersion.Title')}
              description={t('NewerVersion.Description', { version: <b>{serviceDetails.latest.version}</b> })}
              type="warning"
              showIcon
              className={styles.MoreRecentVersion}
              action={
                <Button size="small" type="link" onClick={handleExploreLatestVersion}>
                  {t('NewerVersion.Action')}
                </Button>
              }
            />
          )}


          {/* Image Previews */}
          {(serviceDetails.previewImages?.length || 0) > 0 && (
            <>
              <Divider />
              <div className={styles.PreviewSection}>
                <Carousel autoplay arrows>
                  {serviceDetails.previewImages?.map((image, index) => (
                    <div key={index}>
                      <div className={styles.PreviewImageContainer}>
                        <Image
                          src={getFullAssetPath(image)}
                          alt={`Preview ${index + 1}`}
                          className={styles.PreviewImage}
                          preview={{
                            mask: t.withBaseKey('MarkdownViewer')('ClickToEnlarge'),
                            classNames: {
                              mask: styles.PreviewMask,
                              wrapper: styles.PreviewWrap,
                            },
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </Carousel>
              </div>
            </>
          )}

          <Divider />

          {/* Service Description */}
          <div className={styles.Description}>
            <div className={styles.DescriptionHeader}>
              <h3>{t('AboutThisService')}</h3>
              {inDevMode && (
                <Button
                  icon={<FolderOutlined />}
                  size="small"
                  onClick={handleFileSelection}
                  title="Select README.md file"
                />
              )}
            </div>
            {serviceDetails.docs.contentMd ? (
              <MarkdownViewer
                markdown={serviceDetails.docs.contentMd}
                docsUrl={serviceDetails.docs.url || ''}
                onLinkClick={handleLinkClick}
              />
            ) : (
              <p>
                {serviceDetails.description || 'No description available.'}
              </p>
            )}
          </div>


          {/* Ratings Section */}
          {/* 
          <Divider />
          <div className={styles.RatingsSection}>
            <h3>{t('RatingsAndReviews')}</h3>
          </div> */}

          <Divider />

          {/* Change Log */}
          {/* {serviceDetails.changeLog && (
            <div className={styles.ChangeLog}>
              <h3>{t('WhatIsNew')}</h3>
              <p>
                {serviceDetails.changeLog}
              </p>
            </div>
          )} */}
        </Card>
      )}
    </Container>
  );
};


const LoadingState = () => {
  return (
    <Card>
      {/* Header skeleton */}
      <div className={styles.Header}>
        <div className={classNames(styles.IconWrapper, styles.NoBorder)}>
          <Skeleton.Image active className={styles.IconSkeleton} />
        </div>

        <div className={styles.TitleSection}>
          <Skeleton.Input active size="large" block style={{ width: '60%' }} />
          <Skeleton.Input active size="small" block style={{ width: '30%', marginTop: 8 }} />
          <div style={{ marginTop: 8 }}>
            <Skeleton.Input active size="small" block style={{ width: '40%' }} />
          </div>
        </div>

        <div className={styles.InstallBtnContainer}>
          <Skeleton.Button active size="default" style={{ width: 100 }} />
        </div>
      </div>

      <Divider />

      {/* Preview Images skeleton */}
      <div className={styles.PreviewSection}>
        <div className={styles.PreviewImageContainer}>
          <Skeleton.Image active className={styles.PreviewSkeleton} />
        </div>
      </div>

      <Divider />

      {/* Description skeleton */}
      <div className={styles.Description}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>

      <Divider />

      {/* Ratings skeleton */}
      <div className={styles.RatingsSection}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>

      <Divider />

      {/* Changelog skeleton */}
      <div className={styles.ChangeLog}>
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    </Card>
  );
};


export default HubServiceDetailsPage;
